/**
 * Utilities for parsing and serializing mentions in note content.
 *
 * Supported formats (for parsing):
 * - <Entity type="ahoy_user" entity-id="uuid">Display Name</Entity>
 * - <entity type="ahoy_user" entity-id="uuid">Display Name</entity> (lowercase from nh3)
 * - <Entity type="teammate" entity-id="uuid">Display Name</Entity> (legacy)
 * - <Entity type="user" entity-id="uuid">Display Name</Entity> (legacy)
 * - <NamedEntity type="user" id="uuid">Display Name</NamedEntity> (legacy)
 * - <mention type="teammate" entity-id="uuid">Display Name</mention> (legacy)
 * - <@uuid|Display Name> (legacy Slack-style)
 *
 * Output format (for serialization):
 * - <Entity type="ahoy_user" entity-id="uuid">Display Name</Entity>
 */

import { normalizeEntityType } from '@/lib/search';

// TODO: Use canonical EntityType from @/lib/search/types.ts (see docs/technical-debt/entity-type-consolidation.md)
// Canonical entity types use ahoy_*_entity format to match backend entity_type_name values
export type EntityType =
  | 'ahoy_user'
  | 'ahoy_contact'
  | 'ahoy_company'
  | 'ahoy_deal'
  | 'ahoy_note';

// Regex to match all mention formats (case-insensitive for tag names since nh3 lowercases them):
// 1. <Entity type="X" entity-id="Y">Name</Entity>
// 2. <entity type="X" entity-id="Y">Name</entity> (lowercase from nh3 sanitizer)
// 3. <NamedEntity type="X" id="Y">Name</NamedEntity> (legacy)
// 4. <mention type="X" entity-id="Y">Name</mention> (legacy)
// 5. <@uuid|Display Name> (legacy Slack-style)
const MENTION_REGEX =
  /(?:<(?:mention|NamedEntity|Entity) type="(\w+)" (?:entity-id|id)="([^"]+)">([^<]*)<\/(?:mention|NamedEntity|Entity)>|<@([^|>]+)\|([^>]+)>)/gi;

export interface ParsedMention {
  entityId: string;
  displayName: string;
  type?: EntityType;
}

/**
 * True when a mention `type` uses the disallowed `_timeline` suffix (e.g.
 * `ahoy_contact_timeline`). Those forms are legacy aliases that
 * `normalizeEntityType` still folds to a base entity type for backward compat,
 * but their identifier is a timeline id — NOT an entity id — so treating one as
 * a mention would target the wrong object. The note editor's HTML parser
 * already drops these (rendering the label as plain text); this extractor must
 * drop them too so a raw `_timeline` tag surviving in persisted content (e.g. a
 * title-only note update, where the body is never re-serialized) never becomes
 * a bogus mention association. See the team decision to disallow `_timeline`
 * entity identifiers, mirrored in `noteContentHtml.ts`.
 */
function isTimelineMentionType(type: string): boolean {
  return /_timeline$/i.test(type);
}

/**
 * Extract all mentions from plain text content.
 * Returns unique mentions (deduped by entity ID).
 * Handles all formats: <Entity>, <NamedEntity>, <mention>, and <@uuid|Name>
 */
export function extractMentions(content: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(MENTION_REGEX)) {
    // Match groups:
    // - Groups 1-3: <NamedEntity>/<mention> format (type, id, displayName)
    // - Groups 4-5: Slack-style format (entityId, displayName)
    const isXmlFormat = match[1] !== undefined;
    // Reject disallowed `_timeline` mention types: their identifier is a
    // timeline id, not an entity id, so folding one into a mention would
    // associate the wrong object. Slack-style mentions carry no type and are
    // always `ahoy_user`, so this only applies to the XML formats.
    if (isXmlFormat && isTimelineMentionType(match[1])) {
      continue;
    }
    const entityId = isXmlFormat ? match[2] : match[4];
    const displayName = isXmlFormat ? match[3] : match[5];
    // Normalize to ahoy_*_entity type for consistency
    const type: EntityType = isXmlFormat
      ? normalizeEntityType(match[1])
      : 'ahoy_user';

    if (!seen.has(entityId)) {
      seen.add(entityId);
      mentions.push({ entityId, displayName, type });
    }
  }
  return mentions;
}

/**
 * Extract just the entity IDs from content (for association diffing).
 * Returns unique IDs.
 */
export function extractMentionedUserIds(content: string): string[] {
  return extractMentions(content).map(m => m.entityId);
}

/**
 * Escape HTML special characters in text destined for an HTML element body.
 * Mirrors PlateNoteEditorImpl's local escapeHtml — duplicated here so this
 * module stays self-contained for serialization callers.
 */
function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escape characters that would break out of a double-quoted HTML attribute.
 */
function escapeHtmlAttr(text: string): string {
  return escapeHtmlText(text).replace(/"/g, '&quot;');
}

/**
 * Serialize a mention to Entity format with ahoy_*_entity type.
 *
 * `displayName` and `entityId` are escaped before being interpolated into
 * HTML — a mention's displayName flows from member records (which can carry
 * user-controlled names with characters like `<` or `"`) and an entity id
 * is, by contract, a UUID but we escape it defensively so an unexpected
 * value never breaks out of the attribute.
 */
export function serializeMention(
  entityId: string,
  displayName: string,
  type: EntityType = 'ahoy_user'
): string {
  // Ensure type is normalized to ahoy_ prefix
  const normalizedType = normalizeEntityType(type);
  return `<Entity type="${normalizedType}" entity-id="${escapeHtmlAttr(entityId)}">${escapeHtmlText(displayName)}</Entity>`;
}
